import axios from 'axios';
import { installRequestId } from '../utils/requestId';
import { getMarketplaceApiBaseUrl } from './resolveApiBaseUrl';

/** Public catalog reads — avoid indefinite spinner if items service is down (default axios has no timeout). */
const PUBLIC_GET_TIMEOUT_MS = Number(process.env.REACT_APP_ITEMS_REQUEST_TIMEOUT_MS) || 20000;

const API_BASE_URL = process.env.REACT_APP_ITEMS_API_URL ||
  getMarketplaceApiBaseUrl() || 'http://localhost:8080/api';

// Create axios instance with credentials to send cookies
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send cookies with requests (httpOnly cookies)
});
installRequestId(api);

// Request interceptor - cookies are sent automatically by browser
api.interceptors.request.use(
  (config) => {
    // Cookies are automatically sent when withCredentials: true
    // No need to manually add Authorization header
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const itemsAPI = {
  // Get all items with optional filters
  getItems: async (filters = {}) => {
    const params = new URLSearchParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    
    // Use cookie-authenticated client so blocked sellers are excluded when logged in
    const query = params.toString();
    const url = query ? `/public/items/?${query}` : `/public/items/`;
    const response = await api.get(url, { timeout: PUBLIC_GET_TIMEOUT_MS });
    return response.data;
  },

  /** Approximate viewer lat/lon from IP (items service → ipinfo.io when IPINFO_TOKEN is set). */
  getViewerGeo: async () => {
    const response = await axios.get(`${API_BASE_URL}/public/viewer-geo/`, {
      timeout: Math.min(PUBLIC_GET_TIMEOUT_MS, 8000),
    });
    return response.data;
  },

  // Get single item by ID
  getItemById: async (itemId) => {
    // Use direct axios call for public endpoints (no auth headers)
    const response = await axios.get(`${API_BASE_URL}/public/items/${itemId}/`, {
      timeout: PUBLIC_GET_TIMEOUT_MS,
    });
    return response.data;
  },

  // Get single item by public reference (item_code preferred)
  getItemByRef: async (itemRef) => {
    const response = await api.get(`/items/by-ref/${encodeURIComponent(itemRef)}/`);
    return response.data;
  },

  // Create item without images
  createItem: async (itemData) => {
    const response = await api.post('/items/create/', itemData);
    return response.data;
  },

  /** Create DRAFT listing (metadata only), then upload images one-by-one and finalize. */
  createItemDraft: async (formData) => {
    const response = await api.post('/items/draft/', formData, { timeout: 60000 });
    return response.data;
  },

  uploadItemImage: async (itemId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post(`/items/${itemId}/images/`, formData, { timeout: 90000 });
    return response.data;
  },

  finalizeItemDraft: async (itemId) => {
    const response = await api.post(`/items/${itemId}/finalize/`, {}, { timeout: 30000 });
    return response.data;
  },

  initItemDirectUpload: async (itemId, payload) => {
    const response = await api.post(`/items/${itemId}/uploads/init/`, payload, { timeout: 30000 });
    return response.data;
  },

  signItemDirectParts: async (itemId, payload) => {
    const response = await api.post(`/items/${itemId}/uploads/sign-parts/`, payload, { timeout: 30000 });
    return response.data;
  },

  completeItemDirectUpload: async (itemId, payload) => {
    const response = await api.post(`/items/${itemId}/uploads/complete/`, payload, { timeout: 60000 });
    return response.data;
  },

  abortItemDirectUpload: async (itemId, payload) => {
    await api.post(`/items/${itemId}/uploads/abort/`, payload, { timeout: 15000 });
  },

  /** Item-less ("pending") uploads: start uploading as soon as a photo is picked,
   *  before a draft listing exists. Attach keys at submit via attachPendingImages. */
  initPendingUpload: async (payload) => {
    const response = await api.post('/items/uploads/pending/init/', payload, { timeout: 30000 });
    return response.data;
  },

  signPendingParts: async (payload) => {
    const response = await api.post('/items/uploads/pending/sign-parts/', payload, { timeout: 30000 });
    return response.data;
  },

  completePendingUpload: async (payload) => {
    const response = await api.post('/items/uploads/pending/complete/', payload, { timeout: 60000 });
    return response.data;
  },

  abortPendingUpload: async (payload) => {
    await api.post('/items/uploads/pending/abort/', payload, { timeout: 15000 });
  },

  attachPendingImages: async (itemId, keys) => {
    const response = await api.post(`/items/${itemId}/images/attach-pending/`, { keys }, { timeout: 60000 });
    return response.data;
  },

  // Create item with images (legacy single multipart — prefer draft + finalize on flaky networks)
  createItemWithImages: async (formData) => {
    const response = await api.post('/items/upload/', formData, { timeout: 120000 });
    return response.data;
  },

  // Update item
  updateItem: async (itemId, itemData) => {
    const response = await api.patch(`/items/${itemId}/`, itemData);
    return response.data;
  },

  // Update item by public reference
  updateItemByRef: async (itemRef, itemData) => {
    const response = await api.patch(`/items/by-ref/${encodeURIComponent(itemRef)}/`, itemData);
    return response.data;
  },

  // Add images to existing item
  addItemImages: async (itemId, formData) => {
    const response = await api.post(`/items/${itemId}/images/`, formData, { timeout: 90000 });
    return response.data;
  },

  // Remove image from item
  removeItemImage: async (itemId, imageId) => {
    const response = await api.delete(`/items/${itemId}/images/`, {
      data: { image_id: imageId },
    });
    return response.data;
  },

  // Delete item
  deleteItem: async (itemId) => {
    const response = await api.delete(`/items/${itemId}/`);
    return response.data;
  },

  deleteItemByRef: async (itemRef) => {
    const response = await api.delete(`/items/by-ref/${encodeURIComponent(itemRef)}/`);
    return response.data;
  },

  // Search items
  searchItems: async (searchTerm) => {
    const response = await api.get(`/items/?search=${encodeURIComponent(searchTerm)}`);
    return response.data;
  },

  // Get items by category
  getItemsByCategory: async (category) => {
    const response = await api.get(`/items/?category=${encodeURIComponent(category)}`);
    return response.data;
  },

  // Get items by user (owner_id) — authenticated, used by "My Items" page
  getItemsByUser: async (userId) => {
    const response = await api.get(`/items/?owner_id=${userId}`);
    return response.data;
  },

  // Public catalogue for a user's profile — cookies attached when logged in (block filtering)
  getPublicUserItems: async (userId, page = 1, pageSize = 24) => {
    const response = await api.get(
      `/public/items/?owner_id=${userId}&page=${page}&page_size=${pageSize}`,
      { timeout: PUBLIC_GET_TIMEOUT_MS }
    );
    return response.data?.results ?? response.data ?? [];
  },

  // Request authenticity check
  requestAuthenticityCheck: async (itemId, notes = '') => {
    const response = await api.post(`/items/${itemId}/request-authenticity-check/`, {
      notes: notes
    });
    return response.data;
  },

  requestWeddingFittingAppointment: async (itemId, notes = '') => {
    const response = await api.post(`/items/${itemId}/request-wedding-fitting/`, {
      notes,
    });
    return response.data;
  },

  // Public read of admin-curated featured listings.
  // scope: 'regular_items' | 'defected_items' | 'designer_items' | ...
  // department: '' | Men | Women | Kids | Babies
  getFeatured: async (scope, department = '') => {
    const params = new URLSearchParams({ scope: String(scope) });
    if (department) params.set('department', String(department));
    const url = `/public/featured/?${params.toString()}`;
    const response = await api.get(url, { timeout: PUBLIC_GET_TIMEOUT_MS });
    return response.data;
  },
};
